// React
import React, { useEffect, useRef, useState, useMemo } from 'react';

// Assets
import { CloseCircleOutlined, DeleteFilled } from '@ant-design/icons';

// Components
import IconButton from 'components/@extended/IconButton';

// Packages
import { Loader } from 'components/index';
import {
    Box,
    Button,
    Checkbox,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    FormControl,
    FormControlLabel,
    Grid,
    InputLabel,
    MenuItem,
    Select,
    Stack,
    TextField,
    Tooltip,
    Typography,
    useTheme
} from '@mui/material';
import Autocomplete from '../common/Autocomplete';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DateTimePicker } from '@mui/x-date-pickers';
import InputFieldErrorMessage from '../common/InputFieldErrorMessage';
import { Formik } from 'formik';
import * as Yup from 'yup';
import 'yup-phone-lite';
import store from '../../store';
import CreateUpdateVehicleModal from '../Vehicles/CreateUpdateVehicleModal';
import { openModal } from '../../utils/modal/modal.helper';
import { MODAL_ACTION_TYPE } from '../../utils/constants/modal';
import OccupantVehicleService from '../../services/occupant-vehicle.service';
import AssociateMemberService from '../../services/associate-member.service';
import {
    createVehiclePlate,
    createVehiclePlateForRealtimeDatabase,
    isPlateUnique
} from '../../utils/helpers/vehicle.helper';
import { getOrgVehiclePlates } from '../../store/selectors/Vehicle';

// Actions
import { checkDeviceExists } from 'store/actions/DeviceOperations';
import { fetchUnassignedDevices } from 'store/actions/Managers';

// Styles
import 'react-widgets/scss/styles.scss';

// Utils
import {
    errorMessage,
    unitedStates,
    canadianProvinces,
    plateRegExp,
    universalNameRegExp,
    onlyNumeric,
    userRoles
} from 'utils/constants';
import { useDispatch, useSelector } from 'react-redux';
import DeviceService from 'services/device.service';
import UnassignedDevicesService from 'services/unassigned-devices.service';
import PropTypes from 'prop-types';
import { ownerShape } from 'utils/devices/normalizeOwners';
import { LPR_DEVICE_TYPE } from 'utils/constants/deviceTypes';

const DEVICE_ERROR_MESSAGES = {
    sponsoring_org_required:
        'This person belongs to more than one sponsoring org. Choose the one this device belongs to.',
    sponsoring_org_invalid:
        'The selected sponsoring org no longer applies to this person. Add the device again to choose from the current list.',
    vehicle_not_allowed:
        'A device shared through a sponsoring org cannot carry a vehicle. Remove the vehicle and try again.',
    associate_mobile_credentials_disabled:
        'Mobile credentials are not enabled for associates in this organization yet. Choose another device type.'
};

const errorCodeOf = error => error?.code || error?.message;

const deviceErrorMessage = error => {
    const raw = Array.isArray(error?.message) ? error.message.join(', ') : error?.message;
    return DEVICE_ERROR_MESSAGES[errorCodeOf(error)] || raw;
};

const CreateUpdateDeviceModal = ({
    onClose,
    selectedDevice,
    activeOrg,
    owners,
    propertyId,
    properties,
    type,
    activeOrgId,
    deviceTypes,
    openSnackbar,
    onSuccess,
    onDelete,
    unassignedDevice,
    isNonMemberProperty = false
}) => {
    const allOwners = owners || [];

    // Editing an ALREADY-issued consumer-issued device: owner/property/org are locked (the backend
    // 400s a change to any of the three) — dates, groups and note stay editable.
    const isEditingSharingDevice = type === 'edit' && Boolean(selectedDevice?.sharing);

    const theme = useTheme();
    const formikRef = useRef();
    // Formik runs async validation before `submit`, so two quick clicks can both reach it — the
    // second POST would hit the API's number-uniqueness 400 right after a success snackbar.
    const submitLockRef = useRef(false);
    // Tracks the one-time initial owner/vehicle hydration on edit-load, so the loading guard below
    // only fires while hydrating — not when the user later clears the owner (which is a valid action).
    const ownerHydratedRef = useRef(false);
    const dispatch = useDispatch();

    const showPropertySelector = !!properties;

    const [selectedOwnerEntry, setSelectedOwnerEntry] = useState(null);
    const [selectedPropertyId, setSelectedPropertyId] = useState(propertyId || null);
    const ownerPropertyRole = selectedOwnerEntry?.properties?.[selectedPropertyId]?.role;
    const ownerIsAssociate =
        selectedOwnerEntry?.role === userRoles.ASSOCIATE &&
        (!ownerPropertyRole || ownerPropertyRole === userRoles.ASSOCIATE);
    const [sponsorChoices, setSponsorChoices] = useState([]);
    const [sponsoringOrgId, setSponsoringOrgId] = useState('');

    const [vehiclesReady, setVehiclesReady] = useState(false);
    const [addNewVehicle, setAddNewVehicle] = useState(false);
    const [showVehicle, setShowVehicle] = useState(false);
    const [devicesLoading, setDevicesLoading] = useState(false);
    const [displayFacilityCodes, setDisplayFacilityCode] = useState(null);
    const [isPermanent, setIsPermanent] = useState(true);
    const [vehicles, setVehicles] = useState([
        { key: 'addNew', value: '+ Add New Vehicle' }
    ]);

    const vehicleSectionVisible = deviceType =>
        showVehicle &&
        !isEditingSharingDevice &&
        !(ownerIsAssociate && deviceType !== LPR_DEVICE_TYPE);

    const countries = [
        { value: 'USA', name: 'United States' },
        { value: 'CAN', name: 'Canada' }
    ];

    const orgVehiclePlates = useSelector(state => getOrgVehiclePlates(state.manager));

    const needsSync =
        type === 'edit' &&
        selectedDevice?.owner?.uid &&
        !selectedDevice?.has_ca4k_device &&
        selectedDevice?.device_type !== LPR_DEVICE_TYPE;

    const availableOwners = useMemo(() => {
        if (!selectedPropertyId) return allOwners;
        return allOwners.filter(o =>
            Object.keys(o.properties || {}).includes(selectedPropertyId)
        );
    }, [allOwners, selectedPropertyId]);

    const availableProperties = useMemo(() => {
        if (!properties) return [];
        if (!selectedOwnerEntry) return properties;
        const ownerPropIds = Object.keys(selectedOwnerEntry.properties || {});
        if (ownerPropIds.length === 0) return properties;
        const filtered = properties.filter(p => ownerPropIds.includes(p.id));
        if (propertyId && !filtered.some(p => p.id === propertyId)) {
            const current = properties.find(p => p.id === propertyId);
            if (current) filtered.unshift(current);
        }
        return filtered;
    }, [properties, selectedOwnerEntry, propertyId]);

    const handleClearSelection = setFieldValue => {
        setSelectedOwnerEntry(null);
        setSponsorChoices([]);
        setSponsoringOrgId('');
        setFieldValue('owner', '');
        setAddNewVehicle(false);
        handleFobDevice(setFieldValue);
        if (showPropertySelector) {
            setSelectedPropertyId(null);
            setFieldValue('property_id', null);
        }
    };

    // Ensures an edited device's existing vehicle shows up pre-selected in the "Vehicle
    // Assignment" dropdown (rather than falling back to the inline add-new-vehicle form)
    // even if it isn't already part of the owner's canonical vehicle list.
    const mergeDeviceVehicleIntoOwner = (owner, deviceVehicle) => {
        if (!owner || !deviceVehicle?.tag) return owner;
        const alreadyPresent = (owner.vehicles || []).some(
            v => v.tag?.toUpperCase() === deviceVehicle.tag?.toUpperCase()
        );
        if (alreadyPresent) return owner;
        return { ...owner, vehicles: [...(owner.vehicles || []), deviceVehicle] };
    };

    useEffect(() => {
        if (selectedDevice?.device_type && type === 'edit') {
            const deviceFacility = activeOrg?.device_facility_codes?.find(
                dfc => dfc.device_type === selectedDevice.device_type
            );
            if (deviceFacility && Array.isArray(deviceFacility.facility_codes)) {
                const codes = deviceFacility.facility_codes;
                setDisplayFacilityCode(codes.length > 1 ? codes : null);
            }
        }
    }, [selectedDevice, activeOrg, type]);

    useEffect(() => {
        if (type === 'edit') {
            if (selectedDevice?.expires_at) {
                const expiryDate = new Date(selectedDevice.expires_at);
                setIsPermanent(expiryDate.getFullYear() === 9998);
            } else {
                setIsPermanent(true);
            }
        } else {
            setIsPermanent(true);
        }
    }, [selectedDevice, type]);

    useEffect(() => {
        setVehiclesReady(false);
        if (selectedOwnerEntry?.vehicles?.length) {
            setVehicles([
                { key: 'addNew', value: '+ Add New Vehicle' },
                ...selectedOwnerEntry.vehicles.map(v => ({
                    key: v.tag,
                    value: `${v.year} ${v.make} ${v.model} ${v.tag}`
                }))
            ]);
        } else {
            setVehicles([{ key: 'addNew', value: '+ Add New Vehicle' }]);
        }
        setVehiclesReady(true);
    }, [selectedOwnerEntry]);

    useEffect(() => {
        if (selectedDevice?.device_type === LPR_DEVICE_TYPE) {
            // LPR always captures a vehicle, so the section must show on edit-load even when the
            // device currently has no vehicle (e.g. it was removed from the user's Vehicles tab).
            setShowVehicle(true);
        } else if (selectedDevice?.facility_code) {
            const deviceFacility = activeOrg?.device_facility_codes?.find(dfc =>
                dfc.facility_codes.includes(Number(selectedDevice.facility_code))
            );
            setShowVehicle(deviceFacility?.vehicle || !!selectedDevice?.vehicle);
        } else if (selectedDevice?.vehicle) {
            setShowVehicle(true);
        }

        if (unassignedDevice?.vehicle) {
            setShowVehicle(true);
            setAddNewVehicle(true);
        }

        // Hydrate the owner from the device only once. Guarding on the ref stops a later effect re-run
        // (e.g. activeOrg/members refresh) from re-populating an owner the user has intentionally cleared.
        if (!ownerHydratedRef.current) {
            if (selectedDevice?.owner?.uid) {
                const match = allOwners.find(o => o.uid === selectedDevice.owner.uid);
                if (match) {
                    setSelectedOwnerEntry(
                        mergeDeviceVehicleIntoOwner(match, selectedDevice?.vehicle)
                    );
                    ownerHydratedRef.current = true;
                }
            } else if (selectedDevice?.owner?.invite_id) {
                const match = allOwners.find(
                    o => o.invite_id === selectedDevice.owner.invite_id
                );
                if (match) {
                    setSelectedOwnerEntry(
                        mergeDeviceVehicleIntoOwner(match, selectedDevice?.vehicle)
                    );
                    ownerHydratedRef.current = true;
                }
            }
        }
    }, [selectedDevice, allOwners, activeOrg?.device_facility_codes, unassignedDevice]);

    const calculateDisabledState = (activateAt, expiresAt) => {
        if (!activateAt && !expiresAt) return false;
        const now = new Date();
        const afterActivation = !activateAt || new Date(activateAt) <= now;
        const beforeExpiration = !expiresAt || new Date(expiresAt) >= now;
        return !(afterActivation && beforeExpiration);
    };

    const getRoundedCurrentHour = () => {
        const now = new Date();
        now.setMinutes(0);
        now.setSeconds(0);
        now.setMilliseconds(0);
        return now;
    };

    const submit = async values => {
        if (submitLockRef.current) return;
        submitLockRef.current = true;
        try {
            await submitUnlocked(values);
        } finally {
            submitLockRef.current = false;
        }
    };

    const submitUnlocked = async values => {
        const {
            access_groups,
            org_id,
            device_number,
            device_type,
            expires_at,
            facility_code,
            activate_at,
            property_id,
            vehicle,
            vehicle_color,
            vehicle_make,
            vehicle_model,
            vehicle_state,
            vehicle_tag,
            vehicle_year,
            vehicle_country,
            note
        } = values;

        setDevicesLoading(true);

        const owner = selectedOwnerEntry || selectedDevice?.owner;
        const ownerAccessType =
            owner?.role === userRoles.ASSOCIATE && !ownerIsAssociate
                ? ownerPropertyRole
                : owner?.role;

        // Preserve both invite_id and uid when editing a device
        // Existing devices may contain owner.uid after the invite is accepted,
        // while the selected owner may still contain invite_id. 
        // Merge both sources so we don't accidentally remove either identifier during updates
        const buildOwnerPayload = () => {
            const existingOwner = selectedDevice?.owner || {};
            const selectedOwner = selectedOwnerEntry || {};

            return {
                first_name: selectedOwner.first_name || existingOwner.first_name,
                last_name: selectedOwner.last_name || existingOwner.last_name,
                ...(existingOwner.invite_id && { invite_id: existingOwner.invite_id }),
                ...(selectedOwner.invite_id && { invite_id: selectedOwner.invite_id }),
                ...(existingOwner.uid && { uid: existingOwner.uid }),
                ...(selectedOwner.uid && { uid: selectedOwner.uid })
            };
        };

        const getVehicle = () => {
            // A consumer-issued device never carries a vehicle (the API rejects one — it would be
            // merged into the sponsor's user record).
            if (isEditingSharingDevice) return null;
            if (ownerIsAssociate && device_type !== LPR_DEVICE_TYPE) return null;
            if (!showVehicle || !owner) return null;

            if (addNewVehicle) {
                if (vehicle_tag.trim() === '') return null;
                return {
                    color: vehicle_color,
                    country: vehicle_country || 'USA',
                    make: vehicle_make,
                    model: vehicle_model,
                    state: vehicle_state,
                    tag: vehicle_tag.toUpperCase(),
                    year: vehicle_year
                };
            }

            if (vehicle.trim() === '') return null;
            const found = (owner.vehicles || []).find(
                v =>
                    `${v.year} ${v.make} ${v.model} ${v.tag}`.toLowerCase().trim() ===
                    vehicle.toLowerCase().trim()
            );
            if (found) {
                const { updated_at, ...vehicleWithoutUpdatedAt } = found;
                return vehicleWithoutUpdatedAt;
            }
            return null;
        };

        if (device_type === LPR_DEVICE_TYPE) {
            // LPR has no device number / facility code / access groups, so it skips the CA4K badge
            // sync and posts to its own endpoint. The record still lands in `devices`.
            const lprVehicle = getVehicle();
            const lprDevice = {
                org_id,
                device_type,
                property_id,
                expires_at,
                activate_at,
                access_type: ownerAccessType || selectedDevice?.access_type || '',
                owner: buildOwnerPayload(),
                disabled: calculateDisabledState(activate_at, expires_at),
                vehicle: lprVehicle,
                note: note?.trim() || ''
            };
            const plateLabel = lprVehicle?.tag || 'LPR';

            try {
                if (selectedDevice.id) {
                    await DeviceService.editLprDevice(selectedDevice.id, lprDevice);
                    openSnackbar({
                        open: true,
                        message: `Updated LPR device: <strong>${plateLabel}</strong>`,
                        variant: 'alert',
                        alert: { color: 'success', variant: 'outlined' },
                        close: false
                    });
                } else {
                    await DeviceService.addLprDevice(lprDevice);
                    openSnackbar({
                        open: true,
                        message: `Added LPR device: <strong>${plateLabel}</strong>`,
                        variant: 'alert',
                        alert: { color: 'success', variant: 'outlined' },
                        close: false
                    });
                }
                setDevicesLoading(false);
                onSuccess?.({ device_type });
                onClose();
            } catch (error) {
                const message = Array.isArray(error.message)
                    ? error.message.join(', ')
                    : error.message;
                openSnackbar({
                    open: true,
                    message: `Error: ${message}`,
                    variant: 'alert',
                    alert: { color: 'error', variant: 'outlined' },
                    close: false
                });
                setDevicesLoading(false);
            }
            return;
        }

        if (sponsorChoices.length > 0 && !sponsoringOrgId) {
            openSnackbar({
                open: true,
                message: 'Select the sponsoring org this device belongs to.',
                variant: 'alert',
                alert: { color: 'error', variant: 'outlined' },
                close: false
            });
            setDevicesLoading(false);
            return;
        }

        // Mobile credentials get their number from the backend's allocation engine now — the
        // client never picks or sends one. `deviceNumber` stays null until the create responds
        // with the real assigned number (used only for the success message / onSuccess payload).
        const isMobileCredentialCreate =
            type !== 'edit' && device_type === 'mobile_credential';
        let deviceNumber = isMobileCredentialCreate
            ? null
            : device_number.toString().padStart(5, '0');

        const device = {
            access_groups,
            org_id,
            device_type,
            property_id,
            expires_at,
            activate_at,
            access_type: ownerAccessType || selectedDevice?.access_type || '',
            owner: buildOwnerPayload(),
            disabled: calculateDisabledState(activate_at, expires_at),
            vehicle: getVehicle(),
            note: note?.trim() || '',
            ...(type !== 'edit' && {
                ...(!isMobileCredentialCreate && { device_number: deviceNumber }),
                facility_code: facility_code.toString(),
                ...(sponsoringOrgId && { sponsoring_org_id: sponsoringOrgId })
            })
        };

        try {
            if (selectedDevice.id) {
                await DeviceService.editDevice(selectedDevice.id, device);
                openSnackbar({
                    open: true,
                    message: needsSync
                        ? `Synced device: <strong>${deviceNumber}</strong>`
                        : `Edited device: <strong>${deviceNumber}</strong>`,
                    variant: 'alert',
                    alert: { color: 'success', variant: 'outlined' },
                    close: false
                });
            } else {
                const createdDevice = await DeviceService.addDevice(device);
                // The API drops the matching unassigned device once the badge is assigned.
                dispatch(fetchUnassignedDevices(activeOrgId));
                if (isMobileCredentialCreate) {
                    deviceNumber = createdDevice?.device_number || deviceNumber;
                }
                openSnackbar({
                    open: true,
                    message: `Added device: <strong>${deviceNumber}</strong>`,
                    variant: 'alert',
                    alert: { color: 'success', variant: 'outlined' },
                    close: false
                });
            }
            setDevicesLoading(false);
            onSuccess?.({
            facility_code:
                type === 'edit'
                    ? selectedDevice.facility_code
                    : facility_code.toString(),
            device_number:
                type === 'edit'
                    ? selectedDevice.device_number
                    : deviceNumber
            });
            onClose();
        } catch (error) {
            const code = errorCodeOf(error);
            if (code === 'sponsoring_org_required' && Array.isArray(error.sponsors)) {
                setSponsorChoices(error.sponsors);
                setSponsoringOrgId('');
                openSnackbar({
                    open: true,
                    message: deviceErrorMessage(error),
                    variant: 'alert',
                    alert: { color: 'warning', variant: 'outlined' },
                    close: false
                });
                setDevicesLoading(false);
                return;
            }
            if (code === 'sponsoring_org_invalid') {
                setSponsorChoices([]);
                setSponsoringOrgId('');
            }
            openSnackbar({
                open: true,
                message: `Error: ${deviceErrorMessage(error)}`,
                variant: 'alert',
                alert: { color: 'error', variant: 'outlined' },
                close: false
            });
            setDevicesLoading(false);
        }
    };

    const handleRemoveUnassigned = async () => {
        try {
            setDevicesLoading(true);
            await UnassignedDevicesService.deleteUnassignedDevice(
                activeOrgId,
                unassignedDevice.id
            );
            dispatch(fetchUnassignedDevices(activeOrgId));
            openSnackbar({
                open: true,
                message: `Deleted unassigned device: <strong>${unassignedDevice.device_number}</strong>`,
                variant: 'alert',
                alert: { color: 'success', variant: 'outlined' },
                close: false
            });
            setDevicesLoading(false);
            if (onDelete) onDelete(unassignedDevice.id);
        } catch (error) {
            const message = Array.isArray(error.message)
                ? error.message.join(', ')
                : error.message;
            openSnackbar({
                open: true,
                message: `Error: ${message}`,
                variant: 'alert',
                alert: { color: 'error', variant: 'outlined' },
                close: false
            });
            setDevicesLoading(false);
        }
        onClose();
    };

    const handleRemove = async () => {
        try {
            setDevicesLoading(true);
            await DeviceService.deleteDevice(selectedDevice.id);
            openSnackbar({
                open: true,
                message: `Deleted device: <strong>${selectedDevice.device_number}</strong>`,
                variant: 'alert',
                alert: { color: 'success', variant: 'outlined' },
                close: false
            });
            setDevicesLoading(false);
            if (onDelete) onDelete(selectedDevice.id);
        } catch (error) {
            const message = Array.isArray(error.message)
                ? error.message.join(', ')
                : error.message;
            openSnackbar({
                open: true,
                message: `Error: ${message}`,
                variant: 'alert',
                alert: { color: 'error', variant: 'outlined' },
                close: false
            });
            setDevicesLoading(false);
        }
        onClose();
    };

    const handlePermanentChange = (checked, setFieldValue, setFieldError) => {
        setIsPermanent(checked);
        if (checked) {
            setFieldValue('expires_at', new Date('9998-12-30T00:00:00'));
            setFieldError('expires_at', undefined);
        } else {
            setFieldValue('expires_at', null);
            setFieldError('expires_at', undefined);
        }
    };

    const handleFobDevice = setFieldValue => {
        setFieldValue('vehicle', '');
        setFieldValue('vehicle_color', '');
        setFieldValue('vehicle_make', '');
        setFieldValue('vehicle_model', '');
        setFieldValue('vehicle_state', '');
        setFieldValue('vehicle_tag', '');
        setFieldValue('vehicle_year', '');
        setFieldValue('vehicle_country', 'USA');
    };

    // Opens the same "Add New Vehicle" modal used on the Vehicles tab, scoped to the
    // currently selected owner, instead of the inline mini-form. On success the vehicle
    // is added via the real vehicle API, appended to the local vehicle list, and
    // pre-selected here once the nested modal closes.
    // On a non-member property the owner is an associate member (no user account), so the
    // vehicle goes through the property-scoped associate-members API instead of the
    // occupant (regular user) vehicle API.
    const handleOpenAddVehicleModal = setFieldValue => {
        if (!selectedOwnerEntry?.uid) {
            openSnackbar({
                open: true,
                message:
                    'This owner must accept their invite before a vehicle can be added.',
                variant: 'alert',
                alert: { color: 'error' }
            });
            return;
        }

        const ownerId = selectedOwnerEntry.uid;

        const handleVehicleSubmit = async (vehiclePayload, closeVehicleModal) => {
            const vehicleFields = {
                tag: vehiclePayload.tag,
                make: vehiclePayload.make,
                model: vehiclePayload.model,
                state: vehiclePayload.state,
                ...(vehiclePayload.country != null
                    ? { country: vehiclePayload.country }
                    : {}),
                year:
                    vehiclePayload.year != null && vehiclePayload.year !== ''
                        ? String(vehiclePayload.year)
                        : '',
                color:
                    vehiclePayload.color != null && vehiclePayload.color !== ''
                        ? vehiclePayload.color
                        : ''
            };

            const newPlate = createVehiclePlate(vehicleFields);
            const normalizedNewPlate = createVehiclePlateForRealtimeDatabase(vehicleFields);

            if (!isPlateUnique(normalizedNewPlate, orgVehiclePlates)) {
                openSnackbar({
                    open: true,
                    message: `Vehicle plate "${newPlate}" already exists in the organization`,
                    variant: 'alert',
                    alert: { color: 'error' }
                });
                return;
            }

            try {
                if (isNonMemberProperty) {
                    await AssociateMemberService.addVehicle(
                        activeOrgId,
                        selectedPropertyId || propertyId,
                        ownerId,
                        vehicleFields
                    );
                } else {
                    await OccupantVehicleService.addVehicle(
                        activeOrgId,
                        ownerId,
                        vehicleFields
                    );
                }

                openSnackbar({
                    open: true,
                    message: 'Vehicle added successfully',
                    variant: 'alert',
                    alert: { color: 'success' }
                });

                // getVehicle() (in submit()) looks up the selected vehicle by matching its
                // display string against owner.vehicles, so the new vehicle must be added to
                // selectedOwnerEntry.vehicles itself — not just the local dropdown list —
                // otherwise the lookup misses and the device payload's vehicle ends up null.
                setSelectedOwnerEntry(prev => ({
                    ...prev,
                    vehicles: [...(prev?.vehicles || []), vehicleFields]
                }));

                setFieldValue(
                    'vehicle',
                    `${vehicleFields.year} ${vehicleFields.make} ${vehicleFields.model} ${vehicleFields.tag}`
                );

                closeVehicleModal?.();
            } catch (error) {
                openSnackbar({
                    open: true,
                    message:
                        error?.userMessage || error?.message || 'Failed to add vehicle',
                    variant: 'alert',
                    alert: { color: 'error' }
                });
            }
        };

        openModal(CreateUpdateVehicleModal, {
            vehicle: { country: 'USA', ownerId },
            users: [selectedOwnerEntry],
            modalType: MODAL_ACTION_TYPE.ADD,
            onSubmit: handleVehicleSubmit
        });
    };

    const handleDeviceTypeChange = (
        selectedDeviceType,
        setFieldValue,
        setFieldTouched
    ) => {
        if (selectedDeviceType === LPR_DEVICE_TYPE) {
            // LPR carries no access groups, device number, or facility code. Clear those CA4K-only
            // fields and force the vehicle section on (the plate is the credential) regardless of any
            // facility-code config (the device_type validation skips LPR).
            setDisplayFacilityCode(null);
            setShowVehicle(true);
            setFieldValue('facility_code', null);
            setFieldValue('access_groups', []);
            setFieldValue('device_number', '');
            return;
        }

        const deviceFacility = activeOrg?.device_facility_codes?.find(
            dfc => dfc.device_type === selectedDeviceType
        );

        if (deviceFacility && Array.isArray(deviceFacility.facility_codes)) {
            const codes = deviceFacility.facility_codes;
            if (codes.length > 1) {
                setDisplayFacilityCode(codes);
                setFieldValue('facility_code', null);
            } else if (codes.length === 1) {
                setDisplayFacilityCode(null);
                setFieldValue('facility_code', codes[0]);
            } else {
                setDisplayFacilityCode(null);
                setFieldValue('facility_code', null);
            }
            
            const shouldShowVehicle = deviceFacility.vehicle || false;
            setShowVehicle(shouldShowVehicle);
            if (!shouldShowVehicle) {
                handleFobDevice(setFieldValue);
                setAddNewVehicle(false);
            }
        } else {
            setDisplayFacilityCode(null);
            setShowVehicle(false);
            setFieldValue('facility_code', null);
            handleFobDevice(setFieldValue);
            setAddNewVehicle(false);
        }

        // Mobile credentials no longer get a client-picked device_number — the backend's
        // allocation engine assigns it on create. An existing device keeps its number when its
        // type is flipped, so only a new one is cleared.
        if (selectedDeviceType === 'mobile_credential' && type !== 'edit') {
            setFieldValue('device_number', '');
        }
    };

    // Formik validates each setFieldValue against the values of the previous render,
    // so validating field-by-field while a type change sets device_type, facility_code
    // and device_number races stale snapshots (e.g. "Facility code is required" right
    // after the code was set). Apply every change silently, then validate once with
    // the final values.
    const handleDeviceTypeSelect = async (
        newType,
        values,
        setFieldValue,
        setFieldTouched,
        validateForm
    ) => {
        const patch = { device_type: newType };
        const setSilently = (field, value) => {
            patch[field] = value;
            return setFieldValue(field, value, false);
        };
        const touchSilently = (field, isTouched = true) =>
            setFieldTouched(field, isTouched, false);

        handleDeviceTypeChange(newType, setSilently, touchSilently);
        setSilently('device_type', newType);
        touchSilently('device_type');

        await validateForm({ ...values, ...patch });
    };

    const isDeviceDisabled = selectedDevice?.expires_at
        ? new Date(selectedDevice.expires_at) <= new Date()
        : false;

    const ownerInList =
        allOwners.length > 0 &&
        selectedDevice?.owner &&
        ((selectedDevice.owner.uid &&
            allOwners.some(o => o.uid === selectedDevice.owner.uid)) ||
            (selectedDevice.owner.invite_id &&
                allOwners.some(o => o.invite_id === selectedDevice.owner.invite_id)));
    if (
        selectedDevice?.vehicle &&
        ownerInList &&
        !ownerHydratedRef.current &&
        (!selectedOwnerEntry || !vehiclesReady)
    )
        return <Loader />;

    const initialValues = {
        // Stripped in every mode: a consumer-issued device stores its groups with
        // `reader_time_schedules`, which the device update DTO rejects.
        access_groups: (selectedDevice?.access_groups || []).map(
            ({ reader_time_schedules, ...rest }) => rest
        ),
        org_id: activeOrgId,
        property_id: propertyId || null,
        disabled: type === 'add' ? false : selectedDevice.disabled,
        device_number: selectedDevice?.device_number || '',
        device_type: selectedDevice?.device_type || '',
        expires_at: selectedDevice?.expires_at
            ? new Date(selectedDevice.expires_at)
            : new Date('9998-12-30T00:00:00'),
        facility_code: selectedDevice?.facility_code || null,
        activate_at: selectedDevice?.activate_at
            ? new Date(selectedDevice.activate_at)
            : getRoundedCurrentHour(),
        owner: selectedDevice?.owner
            ? `${selectedDevice.owner.first_name} ${selectedDevice.owner.last_name}`
            : '',
        vehicle: selectedDevice?.vehicle
            ? `${selectedDevice.vehicle.year} ${selectedDevice.vehicle.make} ${selectedDevice.vehicle.model} ${selectedDevice.vehicle.tag}`
            : '',
        vehicle_color: selectedDevice?.vehicle?.color || '',
        vehicle_make: selectedDevice?.vehicle?.make || '',
        vehicle_model: selectedDevice?.vehicle?.model || '',
        vehicle_state: selectedDevice?.vehicle?.state || '',
        vehicle_tag: selectedDevice?.vehicle?.tag || '',
        vehicle_year: selectedDevice?.vehicle?.year || '',
        vehicle_country: selectedDevice?.vehicle?.country || 'USA',
        note: selectedDevice?.note || ''
    };

    const getValidationSchema = () =>
        Yup.object().shape({
            access_groups: Yup.array()
                .of(Yup.object())
                .when('device_type', {
                    is: t => t === LPR_DEVICE_TYPE,
                    then: schema => schema,
                    otherwise: schema =>
                        schema
                            .min(1, errorMessage.device.access_groups.required)
                            .required(errorMessage.device.access_groups.required)
                }),
            disabled: Yup.boolean(),
            property_id: Yup.string().nullable().required('Property is required'),
            // Mobile credentials get their number from the backend's allocation engine on
            // create — no client-side value to require, check length on, or validate against
            // Firestore for conflicts; the server enforces uniqueness and the org's cap.
            device_number: Yup.number()
                .when('device_type', {
                    is: t => t !== 'mobile_credential' && t !== LPR_DEVICE_TYPE,
                    then: schema =>
                        schema.required(errorMessage.device.device_number.required),
                    otherwise: schema => schema
                })
                .test(
                    'len',
                    errorMessage.device.device_number.length,
                    val => !val || val.toString().length <= 5
                )
                .test(
                    'validate-device-number',
                    errorMessage.device.device_number.used,
                    async function (value) {
                        if (
                            this.parent?.device_type === LPR_DEVICE_TYPE ||
                            this.parent?.device_type === 'mobile_credential'
                        )
                            return true;

                        const facilityCode = this.parent?.facility_code;

                        // No facility code -> nothing to check against (the facility_code
                        // field reports its own "required" error).
                        if (!value || !facilityCode) return true;

                        // Leading zeros are valid input ("00012"). Yup casts the
                        // value to a number, so always work with the zero-padded
                        // 5-digit form when comparing / looking numbers up.
                        const startPadded = value.toString().padStart(5, '0');

                        // Editing the same device with the same number/facility code -
                        // nothing to check.
                        if (
                            startPadded === selectedDevice?.device_number?.toString() &&
                            facilityCode?.toString() ===
                                selectedDevice?.facility_code?.toString()
                        )
                            return true;

                        // "Device number is already in use" is checked against the
                        // Firestore `devices` collection, scoped by facility_code +
                        // ca4k_instance. Returns true on conflict.
                        const checkFirestoreConflict = async numPadded => {
                            dispatch(
                                checkDeviceExists(
                                    numPadded,
                                    facilityCode,
                                    activeOrg?.ca4k_instance
                                )
                            );
                            const isValid = await new Promise(resolve => {
                                const unsubscribe = store.subscribe(() => {
                                    const { device } = store.getState();
                                    if (device.deviceExistsValidationChecked) {
                                        resolve(device.isDeviceExists);
                                        unsubscribe();
                                    }
                                });
                            });
                            return isValid === false;
                        };

                        // Regular devices: the number the user typed is either free or
                        // "already in use". Never swap it for another one.
                        const hasConflict = await checkFirestoreConflict(startPadded);
                        if (hasConflict) {
                            const { device } = store.getState();
                            return this.createError({
                                message:
                                    device.deviceExistsValidationError ||
                                    errorMessage.device.device_number.used
                            });
                        }
                        return true;
                    }
                ),
            device_type: Yup.string()
                .trim()
                .required(errorMessage.device.device_type.required)
                .test(
                    'facilityCodesExist',
                    errorMessage.device.device_type.facility_code_missing,
                    function () {
                        const deviceType = this.parent?.device_type;
                        if (!deviceType || deviceType === LPR_DEVICE_TYPE) return true;
                        // The pre-filled type (edit, or add from an existing device) was
                        // never checked against the org config, keep it that way.
                        if (deviceType === selectedDevice?.device_type) return true;
                        // Read the org config directly instead of a React state set
                        // during the type change: that state is still stale in the
                        // validation run triggered by the same change.
                        console.log(activeOrg?.device_facility_codes);
                        return Boolean(
                            activeOrg?.device_facility_codes?.some(
                                dfc => dfc.device_type === deviceType
                            )
                        );
                    }
                ),
            activate_at: Yup.date()
                .required('Activation date & time is required')
                .when('$type', {
                    is: 'add',
                    then: schema =>
                        schema.min(
                            getRoundedCurrentHour(),
                            'Activation date & time cannot be in the past'
                        ),
                    otherwise: schema => schema
                }),
            expires_at: Yup.date()
                .nullable()
                .required('Expiration date & time is required')
                .test(
                    'after-activation',
                    'Expiration date & time must be after activation date & time',
                    function (value) {
                        const { activate_at } = this.parent;
                        if (!value || !activate_at) return true;
                        return new Date(value) > new Date(activate_at);
                    }
                )
                .test(
                    'not-in-past',
                    'Expiration date & time cannot be in the past',
                    function (value) {
                        if (!value) return true;
                        return new Date(value) >= new Date();
                    }
                ),
            facility_code: Yup.number()
                .nullable()
                .when('device_type', {
                    is: t => t === LPR_DEVICE_TYPE,
                    then: schema => schema.notRequired(),
                    otherwise: schema =>
                        schema.required(errorMessage.device.facility_code.required)
                }),
            has_expire: Yup.boolean(),
            owner: Yup.string()
                .trim()
                .lowercase()
                .test('check-members', errorMessage.device.owner.notExists, function () {
                    if (!allOwners || allOwners.length === 0) {
                        return this.createError({
                            message: errorMessage.device.owner.notExists
                        });
                    }
                    return true;
                })
                .when([], {
                    is: () => allOwners && allOwners.length > 0,
                    then: schema => schema.required(errorMessage.device.owner.required)
                }),
            vehicle: Yup.string().test(
                'lpr-vehicle-required',
                errorMessage.device.vehicle.required,
                function (value) {
                    if (this.parent?.device_type !== LPR_DEVICE_TYPE) return true;
                    // For LPR the plate is the credential: accept either a selected existing vehicle
                    // or a newly entered plate (vehicle_tag from the "Add New Vehicle" form).
                    const hasSelected = !!value && value.trim() !== '';
                    const hasNewPlate =
                        !!this.parent?.vehicle_tag &&
                        this.parent.vehicle_tag.trim() !== '';
                    return hasSelected || hasNewPlate;
                }
            ),
            vehicle_color: Yup.string().when([], {
                is: () => addNewVehicle,
                then: schema =>
                    schema.test(
                        'valid-color',
                        'Color is not valid',
                        value => !value || universalNameRegExp.test(value)
                    ),
                otherwise: schema => schema
            }),
            vehicle_make: Yup.string().when([], {
                is: () => addNewVehicle,
                then: schema =>
                    schema
                        .trim()
                        .required(errorMessage.device.vehicle.make.required)
                        .matches(universalNameRegExp, {
                            message: 'Make is not valid',
                            excludeEmptyString: true
                        }),
                otherwise: schema => schema
            }),
            vehicle_model: Yup.string().when([], {
                is: () => addNewVehicle,
                then: schema =>
                    schema
                        .trim()
                        .required(errorMessage.device.vehicle.model.required)
                        .matches(universalNameRegExp, {
                            message: 'Model is not valid',
                            excludeEmptyString: true
                        }),
                otherwise: schema => schema
            }),
            vehicle_state: Yup.string().when([], {
                is: () => addNewVehicle,
                then: schema =>
                    schema
                        .trim()
                        .required(errorMessage.device.vehicle.state.required)
                        .test(
                            'valid-state-province',
                            'Please select a valid state or province',
                            function (value) {
                                if (!value) return true;
                                const list =
                                    this.parent.vehicle_country === 'CAN'
                                        ? canadianProvinces
                                        : unitedStates;
                                return list.some(loc => loc.value === value);
                            }
                        ),
                otherwise: schema => schema
            }),
            vehicle_tag: Yup.string().when([], {
                is: () => addNewVehicle,
                then: schema =>
                    schema
                        .trim()
                        .required(errorMessage.device.vehicle.tag.required)
                        .test(
                            'valid-plate',
                            'Plate is not valid',
                            value => !value || plateRegExp.test(value.toUpperCase())
                        ),
                otherwise: schema => schema
            }),
            vehicle_year: Yup.string().when([], {
                is: () => addNewVehicle,
                then: schema =>
                    schema.test(
                        'valid-year',
                        'Year is not valid',
                        value => !value || onlyNumeric.test(value)
                    ),
                otherwise: schema => schema
            }),
            vehicle_country: Yup.string().when([], {
                is: () => addNewVehicle,
                then: schema =>
                    schema
                        .required(errorMessage.device.vehicle.country.required)
                        .oneOf(
                            countries.map(c => c.value),
                            'Country is not valid'
                        ),
                otherwise: schema => schema
            }),
            note: Yup.string().max(2000, 'Note must be 2000 characters or less')
        });

    return (
        <>
            {devicesLoading && <Loader />}
            <DialogTitle>
                {type === 'edit' ? 'Edit Device' : 'Add New Device'}
            </DialogTitle>
            <Divider />
            <Formik
                initialValues={initialValues}
                onSubmit={submit}
                validationSchema={getValidationSchema()}
                validateOnChange
                innerRef={formikRef}
                initialTouched={(!allOwners || allOwners.length === 0) && { owner: true }}
                initialErrors={
                    (!allOwners || allOwners.length === 0) && {
                        owner: errorMessage.device.owner.notExists
                    }
                }
                context={{ allOwners, type }}
            >
                {({
                    handleSubmit,
                    handleChange,
                    touched,
                    setFieldValue,
                    handleBlur,
                    errors,
                    values,
                    setFieldTouched,
                    setFieldError,
                    validateForm
                }) => (
                    <>
                        <DialogContent sx={{ p: 2.5, maxHeight: '70vh' }}>
                            <Grid container columnSpacing={2}>
                                <Grid item xs={12} sm={6}>
                                    <Stack spacing={0.5}>
                                        <InputLabel htmlFor="device_type">
                                            Device Type
                                        </InputLabel>
                                        <Select
                                            required
                                            variant="outlined"
                                            id="deviceTypeSelect"
                                            name="device_type"
                                            // Guard the LPR boundary on edit: an LPR device can't be
                                            // converted to a badge type (no number/facility/groups), so
                                            // lock it entirely; and a badge device can't be converted TO
                                            // LPR (would orphan its still-active CA4K badge), so the LPR
                                            // option is filtered out of the list below. Badge↔badge stays
                                            // editable.
                                            disabled={
                                                type === 'edit' &&
                                                selectedDevice?.device_type ===
                                                    LPR_DEVICE_TYPE
                                            }
                                            displayEmpty
                                            sx={{ textTransform: 'capitalize' }}
                                            onChange={event =>
                                                handleDeviceTypeSelect(
                                                    event.target.value,
                                                    values,
                                                    setFieldValue,
                                                    setFieldTouched,
                                                    validateForm
                                                )
                                            }
                                            value={values.device_type}
                                            renderValue={selected =>
                                                selected ? (
                                                    deviceTypes[selected]
                                                ) : (
                                                    <Box
                                                        component="span"
                                                        sx={{ color: 'text.disabled' }}
                                                    >
                                                        Select Device Type
                                                    </Box>
                                                )
                                            }
                                            error={Boolean(
                                                touched.device_type &&
                                                    !!errors.device_type
                                            )}
                                        >
                                            {Object.entries(deviceTypes)
                                                .filter(([key]) => {
                                                    // Hide LPR when editing a non-LPR device so a
                                                    // badge device can't be switched to LPR (which
                                                    // would orphan its CA4K badge). Always available
                                                    // on create.
                                                    if (
                                                        type === 'edit' &&
                                                        selectedDevice?.device_type !==
                                                            LPR_DEVICE_TYPE
                                                    ) {
                                                        return key !== LPR_DEVICE_TYPE;
                                                    }
                                                    return true;
                                                })
                                                .map(([key, value]) => (
                                                    <MenuItem
                                                        key={key}
                                                        value={key}
                                                        sx={{
                                                            textTransform: 'capitalize'
                                                        }}
                                                    >
                                                        {value}
                                                    </MenuItem>
                                                ))}
                                        </Select>
                                        <InputFieldErrorMessage
                                            touched={touched.device_type}
                                            error={errors.device_type}
                                        />
                                    </Stack>
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <Stack spacing={0.5}>
                                        <InputLabel htmlFor="owner">Owner</InputLabel>
                                        {isEditingSharingDevice ? (
                                            // Editing an already-issued device: the owner is locked
                                            // (see `isEditingSharingDevice`). It renders display-only,
                                            // same convention as other non-editable fields in this
                                            // modal (kept visually consistent via a disabled
                                            // TextField rather than an editable Autocomplete).
                                            // Default size, not `small`: in this mode the
                                            // editable Autocomplete is never on screen to match, and
                                            // every other field in the dialog is default height.
                                            <TextField
                                                id="owner"
                                                name="owner"
                                                value={values.owner}
                                                disabled
                                                fullWidth
                                            />
                                        ) : (
                                            <>
                                                <Autocomplete
                                                    options={availableOwners}
                                                    getOptionLabel={opt => opt.label}
                                                    placeholder="Select Owner"
                                                    isOptionEqualToValue={(opt, val) =>
                                                        (opt.uid && opt.uid === val.uid) ||
                                                        (opt.invite_id &&
                                                            opt.invite_id ===
                                                                val.invite_id)
                                                    }
                                                    renderOption={(props, opt) => (
                                                        <li
                                                            {...props}
                                                            key={opt.uid || opt.invite_id}
                                                        >
                                                            {opt.label}
                                                        </li>
                                                    )}
                                                    disableCloseOnSelect={false}
                                                    value={selectedOwnerEntry || null}
                                                    onChange={async entry => {
                                                        if (!entry) {
                                                            handleClearSelection(
                                                                setFieldValue
                                                            );
                                                            return;
                                                        }

                                                        const isSameUser =
                                                            (selectedOwnerEntry?.uid &&
                                                                selectedOwnerEntry.uid ===
                                                                    entry.uid) ||
                                                            (selectedOwnerEntry?.invite_id &&
                                                                selectedOwnerEntry.invite_id ===
                                                                    entry.invite_id);

                                                        setSelectedOwnerEntry(entry);
                                                        await setFieldValue(
                                                            'owner',
                                                            entry.label
                                                        );
                                                        if (
                                                            values.property_id &&
                                                            !Object.keys(
                                                                entry.properties || {}
                                                            ).includes(values.property_id)
                                                        ) {
                                                            setFieldValue(
                                                                'property_id',
                                                                null
                                                            );
                                                            setSelectedPropertyId(null);
                                                        }

                                                        if (!isSameUser) {
                                                            setSponsorChoices([]);
                                                            setSponsoringOrgId('');
                                                            setAddNewVehicle(false);
                                                            handleFobDevice(setFieldValue);
                                                        }
                                                    }}
                                                    size="small"
                                                    error={
                                                        touched.owner && !!errors.owner
                                                    }
                                                />
                                                <InputFieldErrorMessage
                                                    touched={touched.owner}
                                                    error={errors.owner}
                                                />
                                                {selectedPropertyId &&
                                                    !availableOwners.length && (
                                                        <Typography
                                                            variant="caption"
                                                            color="text.secondary"
                                                        >
                                                            No owners linked to this
                                                            property.
                                                        </Typography>
                                                    )}
                                            </>
                                        )}
                                    </Stack>
                                </Grid>
                                {showPropertySelector && (
                                    <Grid item xs={12}>
                                        <Stack spacing={0.5}>
                                            <InputLabel>Property</InputLabel>
                                            <Autocomplete
                                                options={availableProperties}
                                                getOptionLabel={opt => opt.address || ''}
                                                renderOption={(props, opt) => (
                                                    <li {...props} key={opt.id}>
                                                        {opt.address || ''}
                                                    </li>
                                                )}
                                                disableCloseOnSelect={false}
                                                value={
                                                    availableProperties.find(
                                                        p => p.id === values.property_id
                                                    ) || null
                                                }
                                                onChange={value => {
                                                    if (!value) {
                                                        handleClearSelection(
                                                            setFieldValue
                                                        );
                                                        return;
                                                    }
                                                    const newId = value.id;
                                                    setFieldValue('property_id', newId);
                                                    setSelectedPropertyId(newId);

                                                    if (
                                                        selectedOwnerEntry &&
                                                        !Object.keys(
                                                            selectedOwnerEntry.properties ||
                                                                {}
                                                        ).includes(newId)
                                                    ) {
                                                        setSelectedOwnerEntry(null);
                                                        setFieldValue('owner', '');
                                                    }
                                                }}
                                                size="small"
                                                disabled={isEditingSharingDevice}
                                                error={
                                                    touched.property_id &&
                                                    !!errors.property_id
                                                }
                                            />
                                            <InputFieldErrorMessage
                                                touched={touched.property_id}
                                                error={errors.property_id}
                                            />
                                            {selectedOwnerEntry &&
                                                availableProperties.length === 0 && (
                                                    <Typography
                                                        variant="caption"
                                                        color="text.secondary"
                                                    >
                                                        This owner has no linked
                                                        properties.
                                                    </Typography>
                                                )}
                                        </Stack>
                                    </Grid>
                                )}
                                {sponsorChoices.length > 0 && (
                                    <Grid item xs={12}>
                                        <Stack spacing={0.5}>
                                            <InputLabel htmlFor="sponsoringOrgSelect">
                                                Sponsoring org
                                            </InputLabel>
                                            <Select
                                                variant="outlined"
                                                id="sponsoringOrgSelect"
                                                name="sponsoring_org_id"
                                                displayEmpty
                                                value={sponsoringOrgId}
                                                onChange={e =>
                                                    setSponsoringOrgId(e.target.value)
                                                }
                                                renderValue={selected =>
                                                    selected ? (
                                                        sponsorChoices.find(
                                                            sponsor =>
                                                                sponsor.id === selected
                                                        )?.name
                                                    ) : (
                                                        <Box
                                                            component="span"
                                                            sx={{
                                                                color: 'text.disabled'
                                                            }}
                                                        >
                                                            Select Sponsoring Org
                                                        </Box>
                                                    )
                                                }
                                            >
                                                {sponsorChoices.map(sponsor => (
                                                    <MenuItem
                                                        key={sponsor.id}
                                                        value={sponsor.id}
                                                    >
                                                        {sponsor.name}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                            <Typography
                                                variant="caption"
                                                color="text.secondary"
                                            >
                                                This person belongs to more than one
                                                sponsoring org. Choose the one this device
                                                belongs to.
                                            </Typography>
                                        </Stack>
                                    </Grid>
                                )}
                                {values.device_type !== LPR_DEVICE_TYPE && (
                                <Grid item xs={12}>
                                    <Stack spacing={0.5}>
                                        <InputLabel htmlFor="access_groups">
                                            Access Groups
                                        </InputLabel>
                                        <Box>
                                            <Autocomplete
                                                multiple
                                                id="access_groups"
                                                options={activeOrg?.access_groups || []}
                                                getOptionLabel={option => option.name}
                                                value={values.access_groups}
                                                isOptionEqualToValue={(option, value) =>
                                                    option.number === value.number
                                                }
                                                onChange={newValue => {
                                                    const updatedValue = newValue.map(
                                                        item => {
                                                            const {
                                                                reader_time_schedules,
                                                                ...rest
                                                            } = item;
                                                            return rest;
                                                        }
                                                    );
                                                    setFieldValue(
                                                        'access_groups',
                                                        updatedValue
                                                    );
                                                }}
                                                error={
                                                    touched.access_groups &&
                                                    errors.access_groups
                                                }
                                                placeholder="Select Access Groups"
                                            />
                                        </Box>
                                        <InputFieldErrorMessage
                                            touched={touched.access_groups}
                                            error={errors.access_groups}
                                        />
                                    </Stack>
                                </Grid>
                                )}
                                {displayFacilityCodes && (
                                    <Grid item xs={12} sm={6}>
                                        <Stack spacing={0.5}>
                                            <InputLabel htmlFor="facility_code">
                                                Facility Code
                                            </InputLabel>
                                            <Select
                                                required
                                                disabled={type === 'edit'}
                                                variant="outlined"
                                                id="facilitySelect"
                                                name="facility_code"
                                                sx={{ textTransform: 'capitalize' }}
                                                onChange={e =>
                                                    setFieldValue(
                                                        'facility_code',
                                                        e.target.value
                                                    )
                                                }
                                                value={values.facility_code}
                                                error={Boolean(
                                                    touched.facility_code &&
                                                        errors.facility_code
                                                )}
                                            >
                                                {displayFacilityCodes.map(code => (
                                                    <MenuItem key={code} value={code}>
                                                        {code}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                            <InputFieldErrorMessage
                                                touched={touched.facility_code}
                                                error={errors.facility_code}
                                            />
                                        </Stack>
                                    </Grid>
                                )}
                                {values.device_type !== LPR_DEVICE_TYPE &&
                                    // Same treatment as LPR: a new mobile credential's number is
                                    // assigned server-side, so there's nothing for this field to
                                    // show at create time — hide it rather than display a disabled
                                    // "Automatically assigned" placeholder. Editing an EXISTING
                                    // mobile credential still shows its real (disabled) number.
                                    !(
                                        type !== 'edit' &&
                                        values.device_type === 'mobile_credential'
                                    ) && (
                                <Grid item xs={12} sm={displayFacilityCodes ? 6 : 12}>
                                    <Stack spacing={0.5}>
                                        <InputLabel>Device Number</InputLabel>
                                        <TextField
                                            required
                                            id="device_numberBasic"
                                            name="device_number"
                                            type="number"
                                            placeholder="Device Number"
                                            fullWidth
                                            autoComplete="number"
                                            onBlur={handleBlur}
                                            disabled={type === 'edit'}
                                            onChange={handleChange}
                                            error={
                                                touched.device_number &&
                                                !!errors.device_number
                                            }
                                            value={values.device_number}
                                        />
                                        <InputFieldErrorMessage
                                            touched={touched.device_number}
                                            error={errors.device_number || ''}
                                        />
                                    </Stack>
                                </Grid>
                                )}
                                {vehicleSectionVisible(values.device_type) && (
                                    <>
                                        {!addNewVehicle ? (
                                            <Grid item xs={12}>
                                                <Stack spacing={0.5}>
                                                    <InputLabel htmlFor="vehicle">
                                                        Vehicle Assignment
                                                    </InputLabel>
                                                    <Select
                                                        required
                                                        variant="outlined"
                                                        id="vehicleSelect"
                                                        name="vehicle"
                                                        disabled={!selectedOwnerEntry}
                                                        displayEmpty
                                                        sx={{
                                                            textTransform: 'capitalize'
                                                        }}
                                                        onChange={e => {
                                                            if (
                                                                e.target.value ===
                                                                '+ Add New Vehicle'
                                                            ) {
                                                                handleOpenAddVehicleModal(
                                                                    setFieldValue
                                                                );
                                                            } else {
                                                                setFieldValue(
                                                                    'vehicle',
                                                                    e.target.value
                                                                );
                                                            }
                                                        }}
                                                        value={values.vehicle}
                                                        renderValue={selected => {
                                                            if (!selectedOwnerEntry) {
                                                                return (
                                                                    <Box
                                                                        component="span"
                                                                        sx={{
                                                                            color: 'text.disabled'
                                                                        }}
                                                                    >
                                                                        Select owner first
                                                                    </Box>
                                                                );
                                                            }
                                                            if (!selected) {
                                                                return (
                                                                    <Box
                                                                        component="span"
                                                                        sx={{
                                                                            color: 'text.disabled'
                                                                        }}
                                                                    >
                                                                        Select Vehicle
                                                                    </Box>
                                                                );
                                                            }
                                                            return selected;
                                                        }}
                                                        error={Boolean(
                                                            touched.vehicle &&
                                                                errors.vehicle
                                                        )}
                                                    >
                                                        {vehicles.map(vehicle => (
                                                            <MenuItem
                                                                key={vehicle.key}
                                                                value={vehicle.value}
                                                                sx={{
                                                                    textTransform:
                                                                        'capitalize'
                                                                }}
                                                            >
                                                                {vehicle.value}
                                                            </MenuItem>
                                                        ))}
                                                    </Select>
                                                    <InputFieldErrorMessage
                                                        touched={touched.vehicle}
                                                        error={errors.vehicle}
                                                    />
                                                </Stack>
                                            </Grid>
                                        ) : (
                                            <Grid item xs={12}>
                                                <Box
                                                    sx={{
                                                        p: 2,
                                                        border: '1px solid',
                                                        borderColor:
                                                            theme.palette.secondary.light,
                                                        borderRadius: 1
                                                    }}
                                                >
                                                    <Grid
                                                        container
                                                        columnSpacing={2}
                                                        rowSpacing={1.5}
                                                    >
                                                        <Grid item xs={12}>
                                                            <Box
                                                                sx={{
                                                                    display: 'flex',
                                                                    justifyContent:
                                                                        'space-between',
                                                                    alignItems: 'center',
                                                                    mt: 1
                                                                }}
                                                            >
                                                                <Typography
                                                                    variant="h6"
                                                                    sx={{
                                                                        color: '#8c8c8c',
                                                                        fontWeight:
                                                                            'semibold'
                                                                    }}
                                                                >
                                                                    Add New Vehicle
                                                                </Typography>
                                                                <Button
                                                                    size="medium"
                                                                    color="secondary"
                                                                    onClick={() => {
                                                                        // Clear ALL vehicle fields
                                                                        // (not just the dropdown) so
                                                                        // stale vehicle_tag can't make
                                                                        // the required-check pass while
                                                                        // getVehicle() returns null.
                                                                        handleFobDevice(
                                                                            setFieldValue
                                                                        );
                                                                        setAddNewVehicle(
                                                                            false
                                                                        );
                                                                    }}
                                                                >
                                                                    <CloseCircleOutlined />
                                                                    <Typography
                                                                        sx={{
                                                                            color: '#8c8c8c',
                                                                            ml: '10px'
                                                                        }}
                                                                    >
                                                                        Cancel
                                                                    </Typography>
                                                                </Button>
                                                            </Box>
                                                        </Grid>
                                                        <Grid item xs={6}>
                                                            <Stack>
                                                                <InputLabel>
                                                                    Color
                                                                </InputLabel>
                                                                <TextField
                                                                    name="vehicle_color"
                                                                    placeholder="Color"
                                                                    fullWidth
                                                                    onBlur={handleBlur}
                                                                    onChange={
                                                                        handleChange
                                                                    }
                                                                    error={
                                                                        touched.vehicle_color &&
                                                                        !!errors.vehicle_color
                                                                    }
                                                                    helperText={
                                                                        touched.vehicle_color &&
                                                                        errors.vehicle_color
                                                                    }
                                                                    value={
                                                                        values.vehicle_color
                                                                    }
                                                                />
                                                            </Stack>
                                                        </Grid>
                                                        <Grid item xs={6}>
                                                            <Stack>
                                                                <InputLabel>
                                                                    Make
                                                                    <Typography
                                                                        component="span"
                                                                        sx={{
                                                                            color: 'error.main',
                                                                            fontSize:
                                                                                '0.75rem',
                                                                            fontWeight:
                                                                                'normal',
                                                                            ml: 0.5
                                                                        }}
                                                                    >
                                                                        *
                                                                    </Typography>
                                                                </InputLabel>
                                                                <TextField
                                                                    name="vehicle_make"
                                                                    placeholder="Make"
                                                                    fullWidth
                                                                    onBlur={handleBlur}
                                                                    onChange={
                                                                        handleChange
                                                                    }
                                                                    error={
                                                                        touched.vehicle_make &&
                                                                        !!errors.vehicle_make
                                                                    }
                                                                    helperText={
                                                                        touched.vehicle_make &&
                                                                        errors.vehicle_make
                                                                    }
                                                                    value={
                                                                        values.vehicle_make
                                                                    }
                                                                />
                                                            </Stack>
                                                        </Grid>
                                                        <Grid item xs={12}>
                                                            <Stack>
                                                                <InputLabel>
                                                                    Model
                                                                    <Typography
                                                                        component="span"
                                                                        sx={{
                                                                            color: 'error.main',
                                                                            fontSize:
                                                                                '0.75rem',
                                                                            fontWeight:
                                                                                'normal',
                                                                            ml: 0.5
                                                                        }}
                                                                    >
                                                                        *
                                                                    </Typography>
                                                                </InputLabel>
                                                                <TextField
                                                                    name="vehicle_model"
                                                                    placeholder="Model"
                                                                    fullWidth
                                                                    onBlur={handleBlur}
                                                                    onChange={
                                                                        handleChange
                                                                    }
                                                                    error={
                                                                        touched.vehicle_model &&
                                                                        !!errors.vehicle_model
                                                                    }
                                                                    helperText={
                                                                        touched.vehicle_model &&
                                                                        errors.vehicle_model
                                                                    }
                                                                    value={
                                                                        values.vehicle_model
                                                                    }
                                                                />
                                                            </Stack>
                                                        </Grid>
                                                        <Grid item xs={6}>
                                                            <Stack>
                                                                <InputLabel>
                                                                    Country
                                                                </InputLabel>
                                                                <Select
                                                                    name="vehicle_country"
                                                                    onChange={e => {
                                                                        handleChange(e);
                                                                        setFieldValue(
                                                                            'vehicle_state',
                                                                            null
                                                                        );
                                                                        setFieldTouched(
                                                                            'vehicle_state',
                                                                            true
                                                                        );
                                                                    }}
                                                                    value={
                                                                        values.vehicle_country
                                                                    }
                                                                >
                                                                    {countries.map(
                                                                        loc => (
                                                                            <MenuItem
                                                                                key={
                                                                                    loc.value
                                                                                }
                                                                                value={
                                                                                    loc.value
                                                                                }
                                                                            >
                                                                                {loc.name}
                                                                            </MenuItem>
                                                                        )
                                                                    )}
                                                                </Select>
                                                            </Stack>
                                                        </Grid>
                                                        <Grid item xs={6}>
                                                            <Stack>
                                                                <InputLabel>
                                                                    {values.vehicle_country ===
                                                                    'CAN'
                                                                        ? 'Province'
                                                                        : 'State'}
                                                                    <Typography
                                                                        component="span"
                                                                        sx={{
                                                                            color: 'error.main',
                                                                            fontSize:
                                                                                '0.75rem',
                                                                            fontWeight:
                                                                                'normal',
                                                                            ml: 0.5
                                                                        }}
                                                                    >
                                                                        *
                                                                    </Typography>
                                                                </InputLabel>
                                                                <Select
                                                                    name="vehicle_state"
                                                                    onBlur={handleBlur}
                                                                    onChange={e =>
                                                                        setFieldValue(
                                                                            'vehicle_state',
                                                                            e.target.value
                                                                        )
                                                                    }
                                                                    value={
                                                                        values.vehicle_state
                                                                    }
                                                                    error={Boolean(
                                                                        touched.vehicle_state &&
                                                                            errors.vehicle_state
                                                                    )}
                                                                >
                                                                    {(values.vehicle_country ===
                                                                    'CAN'
                                                                        ? canadianProvinces
                                                                        : unitedStates
                                                                    ).map(loc => (
                                                                        <MenuItem
                                                                            key={
                                                                                loc.value
                                                                            }
                                                                            value={
                                                                                loc.value
                                                                            }
                                                                        >
                                                                            {loc.name}
                                                                        </MenuItem>
                                                                    ))}
                                                                </Select>
                                                                <InputFieldErrorMessage
                                                                    touched={
                                                                        touched.vehicle_state
                                                                    }
                                                                    error={
                                                                        errors.vehicle_state
                                                                    }
                                                                />
                                                            </Stack>
                                                        </Grid>
                                                        <Grid item xs={6}>
                                                            <Stack>
                                                                <InputLabel>
                                                                    Plate #
                                                                    <Typography
                                                                        component="span"
                                                                        sx={{
                                                                            color: 'error.main',
                                                                            fontSize:
                                                                                '0.75rem',
                                                                            fontWeight:
                                                                                'normal',
                                                                            ml: 0.5
                                                                        }}
                                                                    >
                                                                        *
                                                                    </Typography>
                                                                </InputLabel>
                                                                <TextField
                                                                    name="vehicle_tag"
                                                                    placeholder="Plate/Tag #"
                                                                    fullWidth
                                                                    onBlur={handleBlur}
                                                                    onChange={
                                                                        handleChange
                                                                    }
                                                                    value={
                                                                        values.vehicle_tag
                                                                    }
                                                                    error={Boolean(
                                                                        (touched.vehicle_tag &&
                                                                            errors.vehicle_tag) ||
                                                                            (values.device_type ===
                                                                                LPR_DEVICE_TYPE &&
                                                                                touched.vehicle &&
                                                                                errors.vehicle)
                                                                    )}
                                                                    helperText={
                                                                        (touched.vehicle_tag &&
                                                                            errors.vehicle_tag) ||
                                                                        (values.device_type ===
                                                                            LPR_DEVICE_TYPE &&
                                                                        touched.vehicle &&
                                                                        errors.vehicle
                                                                            ? errorMessage
                                                                                  .vehicle
                                                                                  .tag
                                                                                  .required
                                                                            : '')
                                                                    }
                                                                    inputProps={{
                                                                        style: {
                                                                            textTransform:
                                                                                'uppercase'
                                                                        }
                                                                    }}
                                                                />
                                                            </Stack>
                                                        </Grid>
                                                        <Grid item xs={6}>
                                                            <Stack>
                                                                <InputLabel>
                                                                    Year
                                                                </InputLabel>
                                                                <TextField
                                                                    name="vehicle_year"
                                                                    placeholder="Year"
                                                                    fullWidth
                                                                    onBlur={handleBlur}
                                                                    onChange={
                                                                        handleChange
                                                                    }
                                                                    value={
                                                                        values.vehicle_year
                                                                    }
                                                                    error={Boolean(
                                                                        touched.vehicle_year &&
                                                                            errors.vehicle_year
                                                                    )}
                                                                    helperText={
                                                                        touched.vehicle_year &&
                                                                        errors.vehicle_year
                                                                    }
                                                                />
                                                            </Stack>
                                                        </Grid>
                                                    </Grid>
                                                </Box>
                                            </Grid>
                                        )}
                                    </>
                                )}
                                <Grid item xs={12} sm={5}>
                                    <Stack spacing={0.5}>
                                        <InputLabel>
                                            Activation Date & Time
                                            <Typography
                                                component="span"
                                                sx={{
                                                    color: 'error.main',
                                                    fontSize: '0.75rem',
                                                    fontWeight: 'normal',
                                                    ml: 0.5
                                                }}
                                            >
                                                *
                                            </Typography>
                                        </InputLabel>
                                        <FormControl
                                            sx={{ width: '100%' }}
                                            error={Boolean(
                                                touched.activate_at && errors.activate_at
                                            )}
                                        >
                                            <LocalizationProvider
                                                dateAdapter={AdapterDateFns}
                                            >
                                                <DateTimePicker
                                                    value={values.activate_at}
                                                    views={[
                                                        'year',
                                                        'month',
                                                        'day',
                                                        'hours'
                                                    ]}
                                                    closeOnSelect={false}
                                                    format="MM/dd/yyyy hh:00 a"
                                                    minDateTime={getRoundedCurrentHour()}
                                                    maxDateTime={
                                                        values.expires_at
                                                            ? new Date(values.expires_at)
                                                            : null
                                                    }
                                                    onChange={newValue =>
                                                        setFieldValue(
                                                            'activate_at',
                                                            newValue
                                                        )
                                                    }
                                                    slotProps={{
                                                        textField: {
                                                            placeholder:
                                                                'Select activation date & time',
                                                            error: Boolean(
                                                                touched.activate_at &&
                                                                    errors.activate_at
                                                            )
                                                        },
                                                        actionBar: {
                                                            actions: ['clear', 'accept']
                                                        },
                                                        field: {
                                                            clearable: true,
                                                            onClear: () =>
                                                                setFieldValue(
                                                                    'activate_at',
                                                                    null
                                                                )
                                                        }
                                                    }}
                                                />
                                            </LocalizationProvider>
                                            <InputFieldErrorMessage
                                                touched={touched.activate_at}
                                                error={errors.activate_at}
                                            />
                                        </FormControl>
                                    </Stack>
                                </Grid>

                                <Grid item xs={12} sm={5}>
                                    <Stack spacing={0.5}>
                                        <InputLabel>
                                            Expiration Date & Time
                                            <Typography
                                                component="span"
                                                sx={{
                                                    color: 'error.main',
                                                    fontSize: '0.75rem',
                                                    fontWeight: 'normal',
                                                    ml: 0.5
                                                }}
                                            >
                                                *
                                            </Typography>
                                        </InputLabel>
                                        <FormControl
                                            sx={{ width: '100%' }}
                                            error={Boolean(
                                                touched.expires_at && errors.expires_at
                                            )}
                                        >
                                            <LocalizationProvider
                                                dateAdapter={AdapterDateFns}
                                            >
                                                <DateTimePicker
                                                    value={values.expires_at}
                                                    views={[
                                                        'year',
                                                        'month',
                                                        'day',
                                                        'hours'
                                                    ]}
                                                    format="MM/dd/yyyy hh:00 a"
                                                    minDateTime={
                                                        values.activate_at
                                                            ? new Date(values.activate_at)
                                                            : new Date()
                                                    }
                                                    onChange={newValue =>
                                                        setFieldValue(
                                                            'expires_at',
                                                            newValue
                                                        )
                                                    }
                                                    disabled={isPermanent}
                                                    closeOnSelect={false}
                                                    slotProps={{
                                                        textField: {
                                                            error: Boolean(
                                                                touched.expires_at &&
                                                                    errors.expires_at
                                                            ),
                                                            placeholder: isPermanent
                                                                ? 'Permanent'
                                                                : 'Select expiration date & time',
                                                            value: isPermanent
                                                                ? null
                                                                : values.expires_at
                                                        },
                                                        actionBar: {
                                                            actions: ['clear', 'accept']
                                                        },
                                                        field: {
                                                            clearable: true,
                                                            onClear: () =>
                                                                setFieldValue(
                                                                    'expires_at',
                                                                    null
                                                                )
                                                        }
                                                    }}
                                                />
                                            </LocalizationProvider>
                                            <InputFieldErrorMessage
                                                touched={touched.expires_at}
                                                error={errors.expires_at}
                                            />
                                        </FormControl>
                                    </Stack>
                                </Grid>
                                <Grid item xs={12} sm={2}>
                                    <Stack
                                        spacing={0.5}
                                        sx={{
                                            height: '100%',
                                            justifyContent: 'flex-end',
                                            pb: 3
                                        }}
                                    >
                                        <Tooltip title="Device access never expires.">
                                            <FormControlLabel
                                                control={
                                                    <Checkbox
                                                        checked={isPermanent}
                                                        onChange={e =>
                                                            handlePermanentChange(
                                                                e.target.checked,
                                                                setFieldValue,
                                                                setFieldError
                                                            )
                                                        }
                                                    />
                                                }
                                                label="Permanent"
                                            />
                                        </Tooltip>
                                    </Stack>
                                </Grid>
                                <Grid item xs={12}>
                                    <Stack spacing={0.5}>
                                        <InputLabel htmlFor="note">Note</InputLabel>
                                        <TextField
                                            id="note"
                                            name="note"
                                            placeholder="Add an optional note about this device"
                                            fullWidth
                                            multiline
                                            minRows={1}
                                            maxRows={6}
                                            onBlur={handleBlur}
                                            onChange={handleChange}
                                            error={touched.note && !!errors.note}
                                            value={values.note}
                                            inputProps={{ maxLength: 2000 }}
                                            sx={{
                                                '& .MuiOutlinedInput-root': {
                                                    padding: '10.5px 14px 10.5px 12px'
                                                },
                                                '& .MuiOutlinedInput-input': {
                                                    padding: 0
                                                }
                                            }}
                                        />
                                    </Stack>
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <Stack>
                                        {values.device_type &&
                                            values.device_type !== LPR_DEVICE_TYPE &&
                                            (!activeOrg?.device_facility_codes ||
                                                activeOrg.device_facility_codes.length ===
                                                    0) && (
                                                <Typography
                                                    color="error"
                                                    sx={{ mb: 2 }}
                                                >
                                                    No facility codes available. Please
                                                    contact the administrator to set them
                                                    up before adding a device.
                                                </Typography>
                                            )}
                                    </Stack>
                                </Grid>
                            </Grid>
                        </DialogContent>
                        <Divider />
                        <DialogActions sx={{ p: 2.5 }}>
                            <Grid
                                container
                                justifyContent="space-between"
                                alignItems="center"
                            >
                                <Grid item>
                                    {unassignedDevice && type === 'add' && (
                                        <Tooltip
                                            title="Delete Unassigned Device"
                                            placement="top"
                                        >
                                            <IconButton
                                                size="large"
                                                color="error"
                                                disabled={devicesLoading}
                                                onClick={handleRemoveUnassigned}
                                            >
                                                <DeleteFilled />
                                            </IconButton>
                                        </Tooltip>
                                    )}
                                    {type === 'edit' && (
                                        <Stack
                                            direction="row"
                                            spacing={2}
                                            alignItems="center"
                                        >
                                            <Tooltip
                                                title="Delete Device"
                                                placement="top"
                                            >
                                                <IconButton
                                                    size="large"
                                                    color="error"
                                                    disabled={devicesLoading}
                                                    onClick={handleRemove}
                                                >
                                                    <DeleteFilled />
                                                </IconButton>
                                            </Tooltip>
                                            <Tooltip
                                                title="Disable this device"
                                                placement="top"
                                            >
                                                <Button
                                                    type="button"
                                                    variant="outlined"
                                                    color="warning"
                                                    disabled={
                                                        devicesLoading || isDeviceDisabled
                                                    }
                                                    onClick={() => {
                                                        setIsPermanent(false);
                                                        setFieldValue(
                                                            'expires_at',
                                                            new Date()
                                                        );
                                                        handleSubmit();
                                                    }}
                                                >
                                                    Disable
                                                </Button>
                                            </Tooltip>
                                        </Stack>
                                    )}
                                </Grid>
                                <Grid item>
                                    <Stack direction="row" spacing={2}>
                                        <Button color="error" onClick={onClose}>
                                            Cancel
                                        </Button>
                                        <Button
                                            type="submit"
                                            variant="contained"
                                            onClick={handleSubmit}
                                            disabled={
                                                devicesLoading ||
                                                (values.device_type &&
                                                    values.device_type !== LPR_DEVICE_TYPE &&
                                                    (!activeOrg?.device_facility_codes ||
                                                        activeOrg.device_facility_codes
                                                            .length === 0)) ||
                                                !allOwners ||
                                                allOwners.length === 0
                                            }
                                        >
                                            {needsSync
                                                ? 'Sync'
                                                : type === 'edit'
                                                ? 'Update'
                                                : 'Add'}
                                        </Button>
                                    </Stack>
                                </Grid>
                            </Grid>
                        </DialogActions>
                    </>
                )}
            </Formik>
        </>
    );
};

CreateUpdateDeviceModal.propTypes = {
    onClose: PropTypes.func.isRequired,
    selectedDevice: PropTypes.object.isRequired,
    activeOrg: PropTypes.object,
    owners: PropTypes.arrayOf(ownerShape).isRequired,
    propertyId: PropTypes.string,
    properties: PropTypes.array,
    type: PropTypes.string.isRequired,
    activeOrgId: PropTypes.string.isRequired,
    deviceTypes: PropTypes.oneOfType([PropTypes.array, PropTypes.object]),
    openSnackbar: PropTypes.func,
    onSuccess: PropTypes.func,
    onDelete: PropTypes.func,
    unassignedDevice: PropTypes.object,
    isNonMemberProperty: PropTypes.bool
};

export default CreateUpdateDeviceModal;
